import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../../api";
import type {
  EntityType,
  MigrationRunStatus,
  MyPayEnvironment,
  RunView,
  TargetEnvironment,
} from "../../generated/core-api";
import { onboardingJobHookError } from "../utils/backendErrorMessage";
import { isTerminalJobStatus, useOnboardingJobPoll } from "./useOnboardingJobPoll";

export interface InvokeOnboardingMigrationRunArgs {
  /** Workload the run pushes from — from `useOnboardingWorkload`. */
  workloadId: string;
  targetEnvironment: TargetEnvironment;
  /**
   * Entity types to push. Omitted = "full orchestration profile" — the API
   * resolves every entity type with an ingested record for the workload.
   */
  entityTypes?: EntityType[];
  /**
   * Product-routing target for the push. Required for Slice-4 orchestration —
   * omitting it fails with HTTP 422 (`RunNotOrchestratableError`) before the
   * run is persisted.
   */
  mypayEnvironment?: MyPayEnvironment;
}

export interface OnboardingMigrationRunState {
  invoke: (args: InvokeOnboardingMigrationRunArgs) => void;
  runId: string | null;
  status: MigrationRunStatus | null;
  /** Full read DTO from the latest poll; null until the first successful poll response. */
  data: RunView | null;
  /** The start-run call failing. A terminal `status: "failed"` is surfaced via `status`/`data`, not this field. */
  error: string | null;
  isInvoking: boolean;
  isPolling: boolean;
  reset: () => void;
}

/**
 * Push the workload's validated canonical entities to a target Christmas
 * environment (`POST …/workloads/{workloadId}/runs`) and poll it to terminal
 * (`GET …/workloads/{workloadId}/runs/{runId}`) — the LADR-039 Track 2
 * replacement for the Track 1 `invokeAgent` `mode: "commit"` step (see
 * `.plans/ladr-039-contract-mapping.md`'s shared shape reference).
 *
 * `createOnboardingMigrationRun` returns a `RunAccepted` (`run_id` +
 * `status`, no `entity_types`/`pushed_versions`); the poll route returns the
 * full `RunView` — `data` on this hook is always the latter (or `null`
 * before the first successful poll), matching `useOnboardingImport`/
 * `useOnboardingValidation`'s convention of surfacing the richer read DTO.
 *
 * There is no caller-supplied idempotency/correlation key on
 * `MigrationRunCreate` — resume relies on the API's at-most-one-non-terminal
 * -run invariant plus re-polling an existing `run_id`, not a stored
 * correlation id (see the contract note's "residual mismatches" list).
 */
export function useOnboardingMigrationRun(): OnboardingMigrationRunState {
  const [workloadId, setWorkloadId] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);

  const invokeMutation = useMutation({
    mutationFn: (args: InvokeOnboardingMigrationRunArgs) =>
      api.createOnboardingMigrationRun(args.workloadId, {
        target_environment: args.targetEnvironment,
        entity_types: args.entityTypes,
        mypay_environment: args.mypayEnvironment,
      }),
    onSuccess: (data, variables) => {
      setWorkloadId(variables.workloadId);
      setRunId(data.run_id);
    },
  });

  const pollQuery = useOnboardingJobPoll<RunView>(
    ["onboardingMigrationRun", workloadId, runId],
    () => api.getOnboardingMigrationRun(workloadId as string, runId as string),
    !!workloadId && !!runId
  );

  const status = pollQuery.data?.status ?? null;

  const invoke = useCallback((args: InvokeOnboardingMigrationRunArgs) => {
    setRunId(null);
    invokeMutation.mutate(args);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = useCallback(() => {
    setWorkloadId(null);
    setRunId(null);
    invokeMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    invoke,
    runId,
    status,
    data: pollQuery.data ?? null,
    error: onboardingJobHookError(invokeMutation.error, pollQuery.error),
    isInvoking: invokeMutation.isPending,
    isPolling: !!runId && !isTerminalJobStatus(status) && !pollQuery.isError,
    reset,
  };
}
